import { inject, Injectable, Provider } from '@angular/core';
import { ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';
import { filter, firstValueFrom, map, Observable, Subject } from 'rxjs';
import { promiseWithCatch } from '../common/app.helpers';
import { ToastMessageService } from '../common/toast/toast-service/toast-service.service';
import { RoleInfo } from '../models/role-info.model';
import { createProvider } from '../utils';
import { DialogTypes } from './dialogs/dialogs.enum';

import {
  CreateCategoryDialog,
  DialogActivitiesData,
  DialogCropData,
  DialogDefaultData,
  DialogSelectRoleData,
  DialogSendMessageData,
  InformationMessage,
  SendEmailDialogData,
  SendMessageDialogResult,
  SendReactivationPetitionData,
  UpdateCategoryDialog,
  UserInfoDialogData,
} from 'src/app/interfaces';

import {
  CATEGORY_CREATION_CONTEXT,
  CATEGORY_UPDATION_CONTEXT,
  CROP_IMAGE_DIALOG_DATA,
  DIALOG_ACTIVITIES_DATA,
  DIALOG_DEFAULT_DATA,
  DIALOG_SEND_EMAIL_DATA,
  DIALOG_SEND_MESSAGE_DATA,
  DialogActivitiesComponent,
  DialogAdminDeclineRequestComponent,
  DialogConversationTitleEditComponent,
  DialogCreateCategoryComponent,
  DialogCropComponent,
  DialogDeactivateAccountComponent,
  DialogDefaultComponent,
  DialogIndividualRoleSelectComponent,
  DialogInviteAdminComponent,
  DialogMessageComponent,
  DialogSendReactivationPetitionComponent,
  DialogUpdateCategoryComponent,
  DialogUserInfoComponent,
  INFORMATION_DIALOG_DATA,
  InformationDialogComponent,
  SELECT_ROLE_DIALOG_DATA,
  SEND_EMAIL_DIALOG_DATA,
  USER_INFO_DIALOG_DATA,
} from './dialogs';

// TODO: refactor dialog components, services
// - get rid of the shared state
// - clear result - action => success , dismiss => reject
// - naming - it isn't notifications

export interface DialogContextProvider<TComponent> {
  type: DialogTypes;
  component: TComponent;
  provider?: Provider;
}

export interface ModalDialogResponse {
  type: DialogTypes;
  result: DialogResult;
}

export type DialogResult =
  | boolean
  | string
  | SendMessageDialogResult
  | SendReactivationPetitionData
  | File
  | RoleInfo[]
  | ModalDismissReasons;

export function filterByDialogType<T extends DialogResult>(
  dialogType: DialogTypes,
): (source$: Observable<ModalDialogResponse>) => Observable<T> {
  return (source$) =>
    source$.pipe(
      filter(({ type }: ModalDialogResponse) => type === dialogType),
      map(({ result }: ModalDialogResponse) => result as T),
    );
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private toastService = inject(ToastMessageService);

  private readonly DISMISS_REASON: ModalDismissReasons[] = [
    ModalDismissReasons.BACKDROP_CLICK,
    ModalDismissReasons.ESC,
  ];

  public dialogState: Subject<DialogContextProvider<unknown>> = new Subject();
  public dialogResponse: Subject<ModalDialogResponse> = new Subject();

  public showSuccessfulMessage(message: string): void {
    this.toastService.showSuccessfulMessage(message);
  }

  public showFailureMessage(message: InformationMessage): void {
    this.toastService.showFailureMessage(message);
  }

  public dialogDefault(data: DialogDefaultData): Observable<string | boolean> {
    const type = DialogTypes.DEFAULT;

    return this.openDialog({
      type,
      component: DialogDefaultComponent,
      provider: createProvider(DIALOG_DEFAULT_DATA, { useValue: data }),
    }).pipe(filterByDialogType<string | boolean>(type));
  }

  public dialogSendMessage(data: DialogSendMessageData): Observable<SendMessageDialogResult | boolean> {
    const type = DialogTypes.SEND_MESSAGE;

    return this.openDialog({
      type,
      component: DialogMessageComponent,
      provider: createProvider(DIALOG_SEND_MESSAGE_DATA, { useValue: data }),
    }).pipe(filterByDialogType<SendMessageDialogResult | boolean>(type));
  }

  public dialogIndividualRoleSelect(data: DialogSelectRoleData): Observable<RoleInfo[]> {
    const type = DialogTypes.ROLE_SELECT;

    return this.openDialog({
      type,
      component: DialogIndividualRoleSelectComponent,
      provider: createProvider(SELECT_ROLE_DIALOG_DATA, { useValue: data }),
    }).pipe(filterByDialogType<RoleInfo[]>(DialogTypes.ROLE_SELECT));
  }

  public dialogActivities(data: DialogActivitiesData): Observable<string> {
    const type = DialogTypes.ACTIVITIES;

    return this.openDialog({
      type,
      component: DialogActivitiesComponent,
      provider: createProvider(DIALOG_ACTIVITIES_DATA, { useValue: data }),
    }).pipe(filterByDialogType<string>(type));
  }

  public dialogCrop(data: DialogCropData): Observable<File> {
    const type = DialogTypes.CROP;

    return this.openDialog({
      type,
      component: DialogCropComponent,
      provider: createProvider(CROP_IMAGE_DIALOG_DATA, { useValue: data }),
    }).pipe(filterByDialogType<File>(DialogTypes.CROP));
  }

  public deactivateAccountDialog(data: DialogDefaultData): Observable<string> {
    const type = DialogTypes.DEACTIVATE_ACCOUNT;

    return this.openDialog({
      type,
      component: DialogDeactivateAccountComponent,
      provider: createProvider(DIALOG_DEFAULT_DATA, { useValue: data }),
    }).pipe(filterByDialogType<string>(type));
  }

  public inviteAdminDialog(data: DialogDefaultData): Observable<string> {
    const type = DialogTypes.INVITE_ADMIN;

    return this.openDialog({
      type,
      component: DialogInviteAdminComponent,
      provider: createProvider(DIALOG_DEFAULT_DATA, { useValue: data }),
    }).pipe(filterByDialogType<string>(DialogTypes.INVITE_ADMIN));
  }

  public sendReactivationPetition(data: SendEmailDialogData): Observable<SendReactivationPetitionData> {
    const type = DialogTypes.SEND_REACTIVATION_PETITION;

    return this.openDialog({
      type,
      component: DialogSendReactivationPetitionComponent,
      provider: createProvider(SEND_EMAIL_DIALOG_DATA, { useValue: data }),
    }).pipe(filterByDialogType<SendReactivationPetitionData>(type));
  }

  public declineAccountReactivationPetition(data: SendEmailDialogData): Observable<string> {
    const type = DialogTypes.ADMIN_DECLINE_PETITION;

    return this.openDialog({
      type,
      component: DialogAdminDeclineRequestComponent,
      provider: createProvider(DIALOG_SEND_EMAIL_DATA, { useValue: data }),
    }).pipe(filterByDialogType<string>(type));
  }

  public dialogUserInfoDisplay(data: UserInfoDialogData): Observable<string> {
    const type = DialogTypes.USER_INFO_DISPLAY;

    return this.openDialog({
      type,
      component: DialogUserInfoComponent,
      provider: createProvider(USER_INFO_DIALOG_DATA, { useValue: data }),
    }).pipe(filterByDialogType<string>(type));
  }

  public dialogConversationTitleEdit(data: DialogDefaultData): Observable<string> {
    const type = DialogTypes.CONVERSATION_TITLE_EDIT;

    return this.openDialog({
      type,
      component: DialogConversationTitleEditComponent,
      provider: createProvider(DIALOG_DEFAULT_DATA, { useValue: data }),
    }).pipe(filterByDialogType<string>(type));
  }

  public dialogSaveData(data: DialogDefaultData): Observable<boolean> {
    const type = DialogTypes.SAVE_DATA;

    return this.openDialog({
      type,
      component: DialogDefaultComponent,
      provider: createProvider(DIALOG_DEFAULT_DATA, { useValue: data }),
    }).pipe(filterByDialogType<boolean>(type));
  }

  public updateCategoryDialog(context: UpdateCategoryDialog): Observable<boolean> {
    const type = DialogTypes.UPDATE_CATEGORY;

    return this.openDialog({
      type,
      component: DialogUpdateCategoryComponent,
      provider: createProvider(CATEGORY_UPDATION_CONTEXT, { useValue: context }),
    }).pipe(filterByDialogType<boolean>(type));
  }

  public createCategoryDialog(context: CreateCategoryDialog): Observable<boolean> {
    const type = DialogTypes.CREATE_CATEGORY;

    return this.openDialog({
      type,
      component: DialogCreateCategoryComponent,
      provider: createProvider(CATEGORY_CREATION_CONTEXT, { useValue: context }),
    }).pipe(filterByDialogType<boolean>(type));
  }

  public informationDialog(data: InformationMessage): void {
    const type = DialogTypes.INFORMATION_DIALOG;

    promiseWithCatch(
      firstValueFrom(
        this.openDialog({
          type,
          component: InformationDialogComponent,
          provider: createProvider(INFORMATION_DIALOG_DATA, { useValue: data }),
        }).pipe(filterByDialogType<boolean>(type)),
      ),
    );
  }

  private openDialog<T>(dialog: DialogContextProvider<T>): Observable<ModalDialogResponse> {
    const isBackDropEscEvent: (result: DialogResult) => boolean = (result: DialogResult) =>
      this.DISMISS_REASON.includes(result as ModalDismissReasons);

    this.dialogState.next(dialog);

    return this.dialogResponse.pipe(
      map(({ type, result }: ModalDialogResponse) => {
        return { type, result: !isBackDropEscEvent(result) ? result : false };
      }),
    );
  }
}
